import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ListTree } from "lucide-react";
import { LEGAL_META, LEGAL_PAGES, SITE } from "@/config";
import Reveal from "@/components/Reveal";
import { openCookiePreferences } from "@/components/CookieBanner";
import { scrollToId } from "@/lib/ui";

export default function LegalPage({ pageKey }) {
    const page = LEGAL_PAGES[pageKey];

    useEffect(() => {
        document.title = `${page.title} | ${SITE.name}`;
        return () => {
            document.title = `${SITE.name} | Le migliori offerte Amazon su Telegram`;
        };
    }, [pageKey, page.title]);

    return (
        <main className="mx-auto max-w-3xl px-4 pb-24 pt-28 sm:px-6 sm:pt-36" data-testid="legal-page">
            <Reveal>
                <Link
                    to="/"
                    data-testid="legal-back-home"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-telegram-deep transition-colors hover:text-telegram"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Torna alla home
                </Link>
                <h1 className="mt-6 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">
                    {page.title}
                </h1>
                <p className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-slate-400">
                    Ultimo aggiornamento: {LEGAL_META.lastUpdated}
                </p>
            </Reveal>

            {page.sections.length >= 5 && (
                <Reveal delay={0.08}>
                    <nav
                        data-testid="legal-toc"
                        className="mt-8 rounded-2xl border border-ink/10 bg-white p-6 shadow-[0_10px_30px_-15px_rgba(11,18,32,0.1)]"
                    >
                        <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-telegram-deep">
                            <ListTree className="h-4 w-4" />
                            Indice dei contenuti
                        </p>
                        <ol className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                            {page.sections.map((s, i) => (
                                <li key={s.id}>
                                    <button
                                        onClick={() => scrollToId(`legal-${s.id}`)}
                                        data-testid={`legal-toc-${s.id}`}
                                        className="text-left text-sm font-medium text-slate-600 transition-colors hover:text-telegram-deep"
                                    >
                                        {i + 1}. {s.h}
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </nav>
                </Reveal>
            )}

            <div className="mt-4">
                {page.sections.map((s, i) => (
                    <section
                        key={s.id}
                        id={`legal-${s.id}`}
                        className="scroll-mt-28 pt-9"
                        data-testid={`legal-section-${s.id}`}
                    >
                        <Reveal delay={Math.min(i * 0.03, 0.18)}>
                            <h2 className="font-display text-xl font-semibold tracking-tight text-ink sm:text-2xl">
                                <span className="mr-2 font-mono text-sm font-bold text-telegram-deep">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                {s.h}
                            </h2>
                        </Reveal>
                        {s.ps?.map((p, j) => (
                            <p key={j} className="mt-3 text-[15px] leading-relaxed text-slate-600">
                                {p}
                            </p>
                        ))}
                        {s.list && (
                            <ul className="mt-4 space-y-2.5">
                                {s.list.map((li, j) => (
                                    <li
                                        key={j}
                                        className="flex gap-3 text-[15px] leading-relaxed text-slate-600"
                                    >
                                        <Check strokeWidth={3} className="mt-1 h-4 w-4 shrink-0 text-telegram" />
                                        <span>{li}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {s.ps2?.map((p, j) => (
                            <p key={`b${j}`} className="mt-3 text-[15px] leading-relaxed text-slate-600">
                                {p}
                            </p>
                        ))}
                    </section>
                ))}
            </div>

            <Reveal delay={0.1}>
                <div
                    data-testid="legal-contacts"
                    className="grain mt-12 rounded-3xl bg-ink p-7 text-white sm:p-8"
                >
                    <h3 className="relative z-10 font-display text-lg font-semibold tracking-tight">
                        Contatti del titolare
                    </h3>
                    <div className="relative z-10 mt-4 space-y-1.5 text-sm text-white/70">
                        <p>
                            <span className="font-semibold text-white">Titolare:</span> {LEGAL_META.owner}
                        </p>
                        <p>
                            <span className="font-semibold text-white">Email:</span> {LEGAL_META.email}
                        </p>
                        <p>
                            <span className="font-semibold text-white">Indirizzo:</span> {LEGAL_META.address}
                        </p>
                        <p>
                            <span className="font-semibold text-white">P.IVA:</span> {LEGAL_META.vat}
                        </p>
                    </div>
                    <p className="relative z-10 mt-4 text-xs leading-relaxed text-white/50">
                        {LEGAL_META.note}
                    </p>
                    {pageKey === "cookie" && (
                        <button
                            onClick={openCookiePreferences}
                            data-testid="legal-cookie-preferences"
                            className="relative z-10 mt-5 inline-flex items-center gap-2 rounded-full bg-telegram px-5 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-telegram-glow"
                        >
                            Gestisci preferenze cookie
                        </button>
                    )}
                </div>
            </Reveal>

            <div className="mt-10 border-t border-ink/10 pt-6" data-testid="legal-footer-note">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                    Ultimo aggiornamento: {LEGAL_META.lastUpdated}
                </p>
                <p className="mt-3 text-xs leading-relaxed text-slate-400">
                    Questa pagina può essere aggiornata in qualsiasi momento: ti invitiamo a
                    consultarla periodicamente per restare informato su eventuali modifiche.
                </p>
            </div>
        </main>
    );
}
